import { Box } from '@mui/material';
import MUITypography from '../../../Components/MUITypography';
import { formatAmount, parsePct, SummaryLine } from '../../../Util/UtilHelper';
import Discount from './Discount';
import InvoiceTax from './InvoiceTax';
import { VM_Colors } from '../../../Styles/VMStyles';
import { useMemo } from 'react';

function InvoiceSummary({
  currencySymbol,
  totalVenueCharges,
  totalFoodCharges,
  totalRoomCharges,
  totalPersonCharges,
  totalAdditionalServices,
  totalAmount,
  discountedTotal,
  finalPayableAmount,
  discountType,
  discountValue,
  setDiscountType,
  setDiscountValue,
  taxEnabled,
  setTaxEnabled,
  taxList,
  setTaxList,
}) {
  
// Normalize percentage inputs: treat 0 < v <= 1 as fractions (0.05 => 5%)
const normalizeInputToPct = (v) => {
  const n = typeof v === 'number' ? v : parsePct(v);
  if (!Number.isFinite(n)) return NaN;
  return n > 0 && n <= 1 ? n * 100 : n;
};

const taxAmounts = useMemo(() => {
    const base = Number(totalAmount) || 0;
    return (Array.isArray(taxList) ? taxList : []).map(t => {
      const pct = normalizeInputToPct(t.percentage);
      const amt = (base * (Number.isFinite(pct) ? pct : 0)) / 100;
      return Number.isFinite(amt) ? amt : 0;
    });
  }, [taxList, totalAmount]);


const discountAmount = useMemo(() => {
    const base = Number(totalAmount) || 0;
    const sumTax = taxAmounts.reduce((sum, amt) => sum + amt, 0);
    const target = base + sumTax; // discount applies on original + tax per business rule

    if (discountType === 'percentage') {
      const pct = normalizeInputToPct(discountValue);
      if (!Number.isFinite(pct) || pct <= 0) return 0;
      return (target * pct) / 100;
    }

    // flat discount
    const flat = Number(discountValue) || 0;
    return flat > 0 ? flat : 0;
  }, [discountType, discountValue, totalAmount, taxAmounts]);



  return (
    <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
      <MUITypography variant="h3" mb={1} sx={{fontWeight: 700}}>
        Invoice Summary
      </MUITypography>
    <Box sx={{ p: 2, backgroundColor: '#fff', borderRadius: 2, width: '100%', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' }}>
      {totalVenueCharges > 0 && (
        <SummaryLine label="Venue Charges" value={totalVenueCharges} currencySymbol={currencySymbol} />
      )}
      {totalFoodCharges > 0 && (
        <SummaryLine label="Food Charges" value={totalFoodCharges} currencySymbol={currencySymbol} />
      )}
      {totalRoomCharges > 0 && (
        <SummaryLine label="Room Charges" value={totalRoomCharges} currencySymbol={currencySymbol} />
      )}
      {totalPersonCharges > 0 && (
        <SummaryLine label="Person Charges" value={totalPersonCharges} currencySymbol={currencySymbol} />
      )}
      {totalAdditionalServices > 0 && (
        <SummaryLine label="Additional Services" value={totalAdditionalServices} currencySymbol={currencySymbol} />
      )}

      {/* <SummaryLine
        label="Total Amount"
        value={totalAmount}
        currencySymbol={currencySymbol}
        bold
      /> */}

      {/* Discount input */}
      <Discount
        discountType={discountType}
        discountValue={discountValue}
        onTypeChange={setDiscountType}
        onValueChange={setDiscountValue}
      />

      

      {/* Taxes */}
      <InvoiceTax
        taxEnabled={taxEnabled}
        setTaxEnabled={setTaxEnabled}
        taxList={taxList}
        setTaxList={setTaxList}
        // discountedTotal={discountedTotal}
        totalAmount={totalAmount}
      />


<Box
      sx={{
        backgroundColor: "#fff",
        borderRadius: 3,
        p: { xs: 2, sm: 3 },
        boxShadow: "0px 4px 12px rgba(0,0,0,0.08)",
        maxWidth: "100%",
        width: "100%",
        minWidth: 0,
        boxSizing: "border-box",
      }}
    >
      {/* Original Amount */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="baseline"
        flexWrap="wrap"
        gap={1}
        mb={2}
        sx={{ columnGap: 2, rowGap: 0.5 }}
      >
        <MUITypography fontSize={16} fontWeight={500} sx={{ minWidth: 0, flex: "1 1 auto" }}>
          Original Amount
        </MUITypography>
        <MUITypography fontSize={16} fontWeight={500} sx={{ flexShrink: 0 }}>
          {/* {currencySymbol} */}
          { formatAmount(totalAmount.toFixed(2) ,currencySymbol)}
        </MUITypography>
      </Box>

       <Box
        display="flex"
        justifyContent="space-between"
        alignItems="baseline"
        flexWrap="wrap"
        gap={1}
        mb={2}
        sx={{ columnGap: 2, rowGap: 0.5 }}
      >
        <MUITypography fontSize={16} fontWeight={500} sx={{ minWidth: 0, flex: "1 1 auto" }}>
          Tax Amount
        </MUITypography>
        <MUITypography fontSize={16} fontWeight={500} sx={{ flexShrink: 0 }}>
          {/* {currencySymbol} */}
          { formatAmount(taxAmounts.reduce((sum, amt) => sum + amt, 0).toFixed(2) ,currencySymbol)}
        </MUITypography>
      </Box>
 <Box
        sx={{
          borderTop: "1px dashed #ccc",
          my: 2,
        }}
      /> 
  <Box
        display="flex"
        justifyContent="space-between"
        alignItems="baseline"
        flexWrap="wrap"
        gap={1}
        mb={2}
        sx={{ columnGap: 2, rowGap: 0.5 }}
      >
        <MUITypography fontSize={16} fontWeight={500} color={VM_Colors.primary} sx={{ minWidth: 0, flex: "1 1 auto", wordBreak: "break-word" }}>
          Total Amount
        </MUITypography>
        <MUITypography fontSize={16} fontWeight={500} color={VM_Colors.primary} sx={{ flexShrink: 0 }}>
          {`${formatAmount((totalAmount + taxAmounts.reduce((sum, amt) => sum + amt, 0)).toFixed(2),currencySymbol)}`}
        </MUITypography>
      </Box>


{discountAmount > 0 && (
  <Box
        display="flex"
        justifyContent="space-between"
        alignItems="baseline"
        flexWrap="wrap"
        gap={1}
        mb={2}
        sx={{ columnGap: 2, rowGap: 0.5 }}
      >
        <MUITypography fontSize={16} fontWeight={500} sx={{ minWidth: 0, flex: "1 1 auto", wordBreak: "break-word" }}>
          {`Discount Applied (${discountType === 'percentage' ? `${discountValue}%` : formatAmount(discountValue, currencySymbol)})`}
        </MUITypography>
        <MUITypography fontSize={16} fontWeight={500} color={VM_Colors.error} sx={{ flexShrink: 0 }}>
          {` - ${formatAmount(discountAmount, currencySymbol)}`}
        </MUITypography>
      </Box>
      )}


      {/* GST */}
      {/* <Box display="flex" justifyContent="space-between" mb={2}>
        <MUITypography fontSize={16} fontWeight={500}>
          GST ({gstPercentage}%)
        </MUITypography>
        <MUITypography fontSize={16} fontWeight={500}>
          +{currencySymbol}
          {gstAmount.toLocaleString("en-IN", {
            minimumFractionDigits: 2,
          })}
        </MUITypography>
      </Box>

     */}
      <Box
        sx={{
          borderTop: "1px dashed #ccc",
          my: 2,
        }}
      /> 

      {/* Final Amount */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1.5}
        sx={{ columnGap: 2, rowGap: 1 }}
      >
        <MUITypography fontSize={18} fontWeight={700} sx={{ minWidth: 0 }}>
          Final Amount Payable
        </MUITypography>
        <MUITypography
          fontSize={{ xs: 22, sm: 26 }}
          fontWeight={800}
          sx={{ color: "#16a34a", flexShrink: 0 }}
        >
          {/* {currencySymbol} */}
          { formatAmount(finalPayableAmount.toFixed(2),currencySymbol) }
        </MUITypography>
      </Box>
    </Box>


    </Box>
   </Box>
 
  );
}

export default InvoiceSummary;